import { useEffect, useRef, useState } from "react";

/**
 * Hook encapsulating @mention autocomplete logic for Light note textareas.
 * Handles: mention detection on typing, keyboard navigation, insertion, click-outside-to-close.
 *
 * @param {Array} accountUsers - array of { id/userId, firstname, name } objects
 * @param {string} noteText - current textarea value (controlled externally)
 * @param {function} setNoteText - setter for the textarea value
 * @returns {{ mentionQuery, mentionIndex, selectedMentions, textareaRef, dropdownRef, filteredUsers, insertMention, handleTextChange, handleKeyDown, resetMentions }}
 */
export default function useMentionAutocomplete(accountUsers, noteText, setNoteText) {
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (mentionQuery === null) return;
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        textareaRef.current && !textareaRef.current.contains(e.target)
      ) setMentionQuery(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mentionQuery]);

  // Pre-filtered users list
  const filteredUsers = (accountUsers ?? []).filter((u) =>
    mentionQuery !== null &&
    `${u.firstname ?? ""} ${u.name ?? ""}`.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const insertMention = (u) => {
    const full = `${u.firstname ?? ""} ${u.name ?? ""}`.trim();
    const ta = textareaRef.current;
    const pos = ta.selectionStart;
    const before = noteText.slice(0, pos);
    const atIdx = before.lastIndexOf("@");
    const after = noteText.slice(pos);
    const inserted = `@${full} `;
    setNoteText(before.slice(0, atIdx) + inserted + after);
    setSelectedMentions((prev) => [...prev, { nomPrenom: full, userOid: u.id ?? u.userId }]);
    setMentionQuery(null);
    setTimeout(() => { ta.focus(); const np = atIdx + inserted.length; ta.setSelectionRange(np, np); }, 0);
  };

  /** Textarea onChange handler — detects @ mentions while typing */
  const handleTextChange = (e) => {
    const val = e.target.value;
    setNoteText(val);
    const pos = e.target.selectionStart;
    const before = val.slice(0, pos);
    const atIdx = before.lastIndexOf("@");
    const mentionText = before.slice(atIdx + 1);
    if (atIdx !== -1 && mentionText.split(" ").length <= 2) {
      setMentionQuery(mentionText);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  /**
   * Textarea onKeyDown handler — arrow navigation, Enter to select, Escape to close, Ctrl+Enter to submit.
   * @param {KeyboardEvent} e
   * @param {function} onSubmit - called on Ctrl/Cmd + Enter
   */
  const handleKeyDown = (e, onSubmit) => {
    if (mentionQuery !== null && filteredUsers.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => Math.min(i + 1, filteredUsers.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter") { e.preventDefault(); insertMention(filteredUsers[mentionIndex]); return; }
      if (e.key === "Escape") { setMentionQuery(null); return; }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) onSubmit();
  };

  const resetMentions = () => setSelectedMentions([]);

  return {
    mentionQuery, mentionIndex, selectedMentions,
    textareaRef, dropdownRef, filteredUsers,
    insertMention, handleTextChange, handleKeyDown, resetMentions,
  };
}
